import { useMemo } from "react";
import classNames from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import clientService from "api/clientService";
import {
  EParticipantVersionChangeKind,
  IClientVersion,
} from "dto/IClient";
import Chip from "components/Chip";
import Loading from "components/Loading";
import NotFoundScreen from "components/NotFoundScreen";
import { getErrorMessage } from "utils/getErrorMessage";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

type VersionField =
  | "Name"
  | "Address"
  | "Email"
  | "Phone"
  | "EDeliveryAddress"
  | "EpuapAddress"
  | "NIP"
  | "REGON"
  | "KRS";

/** Pola pokazywane w wersji — w kolejności jak na karcie podmiotu. */
const FIELDS: { key: VersionField; label: string }[] = [
  { key: "Name", label: "common.name" },
  { key: "Address", label: "forms.address" },
  { key: "Email", label: "forms.email" },
  { key: "Phone", label: "forms.phone" },
  { key: "EDeliveryAddress", label: "forms.eDeliveryAddress" },
  { key: "EpuapAddress", label: "clients.history.epuapAddress" },
  { key: "NIP", label: "forms.nip" },
  { key: "REGON", label: "forms.regon" },
  { key: "KRS", label: "forms.krs" },
];

const KIND_LABELS: Record<EParticipantVersionChangeKind, string> = {
  [EParticipantVersionChangeKind.Created]: "clients.history.kind.created",
  [EParticipantVersionChangeKind.Changed]: "clients.history.kind.changed",
  [EParticipantVersionChangeKind.Initial]: "clients.history.kind.initial",
  [EParticipantVersionChangeKind.ImportedFromMagistrat]:
    "clients.history.kind.imported",
};

const formatTimestamp = (value?: string | null) =>
  value ? moment(value).format("DD.MM.YYYY HH:mm") : "";

const ClientVersionsHistory: FC<Props> = ({ clientId, revision }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } =
    clientService.useGetClientVersions(clientId, revision, !!clientId);

  const versions: IClientVersion[] = useMemo(() => data?.data ?? [], [data]);

  if (isLoading) {
    return <Loading withoutText />;
  }

  if (isError) {
    return (
      <div className={styles.empty}>
        <NotFoundScreen
          size="small"
          title={t("clients.history.errorTitle")}
          text={getErrorMessage(error ?? undefined)}
        />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className={styles.empty}>
        <NotFoundScreen
          size="small"
          title={t("clients.history.emptyTitle")}
          text={t("clients.history.emptyText")}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {versions.map((version, index) => {
        // lista jest od najnowszej — poprzednia (starsza) wersja leży pod spodem
        const previous = versions[index + 1];
        const period = version.IsCurrent
          ? t("clients.history.since", {
              date: formatTimestamp(version.ValidFrom),
            })
          : `${formatTimestamp(version.ValidFrom)} – ${formatTimestamp(version.ValidTo)}`;

        return (
          <div
            key={version.Id}
            className={classNames(
              styles.version,
              version.IsCurrent && styles.current,
            )}
          >
            <div className={styles.header}>
              <div className={styles.title}>
                {t("clients.history.version", { no: version.VersionNo })}
              </div>
              {version.IsCurrent && (
                <Chip small element={<>{t("clients.history.current")}</>} />
              )}
              <div className={styles.kind}>
                {t(KIND_LABELS[version.ChangeKind] ?? KIND_LABELS[1])}
              </div>
            </div>

            <div className={styles.meta}>
              {period}
              {version.CreatedByName &&
                ` · ${t("clients.history.changedBy", {
                  name: version.CreatedByName,
                })}`}
            </div>

            <div className={styles.fields}>
              {FIELDS.map(({ key, label }) => {
                const value = version[key];
                const before = previous?.[key];
                const changed = !!previous && (value ?? "") !== (before ?? "");

                if (!value && !changed) {
                  return null;
                }

                return (
                  <div
                    key={key}
                    className={classNames(
                      styles.field,
                      changed && styles.changed,
                    )}
                  >
                    <span className={styles.label}>{t(label)}</span>
                    <span className={styles.value}>
                      {value || t("clients.history.removed")}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className={styles.footer}>
              {t("clients.history.parcels", { count: version.ParcelsCount })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ClientVersionsHistory;
